import type { Fetched, WebPushSubscription } from "./platform-client.js";

/**
 * Turning a browser into a doorbell.
 *
 * The permission prompt is the one thing here that must only ever follow a
 * click: a page that asks for notifications on load is asking to be blocked
 * for good. So `enableWebPush` is called from the account dialog's button and
 * nowhere else, while `refreshWebPush` re-registers an existing subscription
 * silently — no prompt, no new subscription — so a rotated endpoint reaches the
 * server without the reader doing anything.
 *
 * Every browser and network dependency is injected so the flow can be driven
 * end to end without a push service, which is the only way to test the
 * failure branches a real one would make rare.
 */

export type PushOutcome =
  | { readonly kind: "enabled" }
  | { readonly kind: "unsupported" }
  | { readonly kind: "denied" }
  | { readonly kind: "unconfigured" }
  | { readonly kind: "failed"; readonly detail: string };

/** The subset of `PushSubscription` this module reads. */
export interface SubscriptionLike {
  readonly endpoint: string;
  toJSON(): { readonly endpoint?: string; readonly keys?: Record<string, string> };
}

export interface PushManagerLike {
  getSubscription(): Promise<SubscriptionLike | null>;
  subscribe(options: { userVisibleOnly: true; applicationServerKey: Uint8Array<ArrayBuffer> }): Promise<SubscriptionLike>;
}

export interface WebPushDeps {
  /** Undefined when this browser cannot do Web Push at all. */
  readonly pushManager: () => Promise<PushManagerLike | undefined>;
  readonly permission: () => NotificationPermission;
  readonly requestPermission: () => Promise<NotificationPermission>;
  readonly publicKey: () => Promise<Fetched<{ public_key: string }>>;
  readonly register: (subscription: WebPushSubscription) => Promise<Fetched<unknown>>;
}

/** A base64url VAPID key as `applicationServerKey` wants it: raw bytes. */
export function decodeVapidKey(key: string): Uint8Array<ArrayBuffer> {
  const normalized = key.trim().replaceAll("-", "+").replaceAll("_", "/");
  const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let index = 0; index < binary.length; index++) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

/** Reads the keys a subscription exposes; undefined when either is missing. */
export function subscriptionKeys(subscription: SubscriptionLike): WebPushSubscription | undefined {
  const json = subscription.toJSON();
  const p256dh = json.keys?.["p256dh"];
  const auth = json.keys?.["auth"];
  if (!p256dh || !auth) return undefined;
  return { endpoint: subscription.endpoint, keys: { p256dh, auth } };
}

async function registerSubscription(deps: WebPushDeps, subscription: SubscriptionLike): Promise<PushOutcome> {
  const keys = subscriptionKeys(subscription);
  if (!keys) return { kind: "failed", detail: "This browser's subscription carries no keys." };
  const registered = await deps.register(keys);
  if (registered.ok) return { kind: "enabled" };
  if (registered.reason === "unavailable") return { kind: "unconfigured" };
  return { kind: "failed", detail: registered.detail };
}

/** From a click only: asks permission, subscribes, and registers. */
export async function enableWebPush(deps: WebPushDeps): Promise<PushOutcome> {
  const manager = await deps.pushManager();
  if (!manager) return { kind: "unsupported" };
  if (deps.permission() === "denied") return { kind: "denied" };
  const key = await deps.publicKey();
  if (!key.ok) return key.reason === "unavailable" ? { kind: "unconfigured" } : { kind: "failed", detail: key.detail };
  const permission = deps.permission() === "granted" ? "granted" : await deps.requestPermission();
  if (permission !== "granted") return { kind: "denied" };
  let subscription = await manager.getSubscription();
  if (!subscription) {
    try {
      subscription = await manager.subscribe({ userVisibleOnly: true, applicationServerKey: decodeVapidKey(key.value.public_key) });
    } catch (error) {
      return { kind: "failed", detail: error instanceof Error ? error.message : "The browser refused to subscribe." };
    }
  }
  return registerSubscription(deps, subscription);
}

/**
 * Silent upkeep: re-registers a subscription that already exists. Never
 * prompts and never subscribes, so it is safe to run on every session open.
 */
export async function refreshWebPush(deps: WebPushDeps): Promise<PushOutcome | undefined> {
  const manager = await deps.pushManager();
  if (!manager || deps.permission() !== "granted") return undefined;
  const subscription = await manager.getSubscription();
  if (!subscription) return undefined;
  return registerSubscription(deps, subscription);
}

/** What the account dialog says about notifications, before and after. */
export function pushStatusLabel(outcome: PushOutcome | undefined, permission: NotificationPermission | undefined): string {
  if (outcome) {
    switch (outcome.kind) {
      case "enabled": return "Alerts will notify this browser, even when RecallVault is closed.";
      case "unsupported": return "This browser cannot receive push notifications.";
      case "denied": return "Notifications are blocked for this site. Allow them in the browser's site settings, then try again.";
      case "unconfigured": return "The hosted vault does not send Web Push yet.";
      case "failed": return `Notifications could not be turned on. ${outcome.detail}`;
    }
  }
  if (permission === "granted") return "Notifications are allowed. Turn them on to register this browser.";
  if (permission === "denied") return "Notifications are blocked for this site.";
  return "Get an alert here when a run fails or a reply arrives, even with RecallVault closed.";
}
